import { useState, useMemo, useCallback, memo } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Button,
    Dialog,
    Typogaphy,
    GridInformation,
    FormItem,
} from 'vnetwork-ui'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import i18n from '@/configs/i18n'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { TextareaAutoResize } from '@/components/ui/textarea'
import { Business } from '@/modules/Business/types/response'
import { useRefetchBusinessQueries } from '@/modules/Business/types/utils'
import {
    BusinessApprovalModalProps,
    SingleApprovalProps,
    BulkApprovalProps,
    ApprovalFormData,
} from './types'
import { createBusinessColumns } from './columns'
import { useBusinessApproval } from './use-business-approval'

const approvalSchema = yup.object().shape({
    feedback: yup.string().trim().required(i18n.t('blank-errors')),
})

// Single mode component
const SingleApprovalContent: React.FC<
    SingleApprovalProps & { onClose?: () => void }
> = (props) => {
    const { t } = useTranslation('business')
    const { refetchAll } = useRefetchBusinessQueries()
    const {
        type,
        feedbackInitial,
        businessStatus,
        businessLeaderStatus = '',
        businessBodStatus = '',
        refetch,
        onClose,
    } = props

    const { control, handleSubmit } = useForm<ApprovalFormData>({
        defaultValues: {
            feedback: feedbackInitial || 'approved',
        },
        resolver: yupResolver(approvalSchema),
    })

    const handleSuccess = useCallback(() => {
        refetch()
        refetchAll()
        onClose?.()
    }, [refetch, refetchAll, onClose])

    const { isPending, handleSingleApprove, handleSingleReject } =
        useBusinessApproval({
            mode: 'single',
            type,
            onSuccess: handleSuccess,
        })

    // Compute disabled state
    const isDisabled = useMemo(() => {
        if (type === 'leader') {
            return (
                ['approved', 'cancelled', 'rejected'].includes(
                    businessLeaderStatus
                ) ||
                ['approved', 'cancelled'].includes(businessStatus) ||
                businessBodStatus === 'approved'
            )
        }
        return (
            ['approved'].includes(businessBodStatus) ||
            ['cancelled', 'rejected'].includes(businessStatus)
        )
    }, [type, businessLeaderStatus, businessBodStatus, businessStatus])

    const isTextareaDisabled = useMemo(() => {
        if (type === 'leader') {
            return (
                isDisabled ||
                ['approved', 'cancelled', 'rejected'].includes(
                    businessBodStatus
                ) ||
                ['approved', 'cancelled', 'rejected'].includes(businessStatus)
            )
        }
        return (
            ['approved'].includes(businessBodStatus) ||
            ['cancelled', 'rejected'].includes(businessStatus)
        )
    }, [type, isDisabled, businessBodStatus, businessStatus])

    const onSubmit = useCallback(
        (data: ApprovalFormData) => {
            handleSingleApprove(props, data.feedback)
        },
        [handleSingleApprove, props]
    )

    const onReject = useCallback(() => {
        handleSingleReject(props)
    }, [handleSingleReject, props])

    return (
        <div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <GridInformation
                    data={[
                        {
                            label: '',
                            value: (
                                <FormItem
                                    control={control}
                                    name="feedback"
                                    type="textarea"
                                    disabled={isTextareaDisabled}
                                    label={t('feedback')}
                                />
                            ),
                            className: 'col-span-4',
                        },
                    ]}
                    head={{
                        title:
                            type === 'leader'
                                ? t('leader-confirm')
                                : t('bom/bod-confirm'),
                    }}
                />
            </form>
            <div className="p-4 flex items-center justify-end gap-4 bg-white rounded-[8px] mt-2">
                <Button
                    variant="outlined"
                    size="small"
                    color="info"
                    loading={isPending}
                    onClick={onReject}
                    disabled={isDisabled}
                >
                    {t('reject_business')}
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    loading={isPending}
                    onClick={handleSubmit(onSubmit)}
                    disabled={isDisabled}
                >
                    {t('confirm')}
                </Button>
            </div>
        </div>
    )
}

// Bulk mode component
const BulkApprovalContent: React.FC<
    BulkApprovalProps & { onClose?: () => void }
> = (props) => {
    const { t } = useTranslation('business')
    const { rows, refetch, onClose } = props
    const [feedback, setFeedback] = useState('approved')

    const handleSuccess = useCallback(() => {
        refetch()
        onClose?.()
    }, [refetch, onClose])

    const handlePartialSuccess = useCallback(() => {
        refetch()
    }, [refetch])

    const { isPending, handleBulkApprove } = useBusinessApproval({
        mode: 'bulk',
        onSuccess: handleSuccess,
        onPartialSuccess: handlePartialSuccess,
    })

    const columns = useMemo(() => createBusinessColumns(t), [t])
    const getRowKey = useCallback(
        (row: Business) => String(row.business_id),
        []
    )
    const handleFeedbackChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setFeedback(e.target.value),
        []
    )

    const pagination = useMemo(
        () => ({
            page: 1,
            pageSize: 5,
            total: rows?.length || 0,
            pageSizeOptions: [5, 10, 20],
        }),
        [rows?.length]
    )

    const onApprove = useCallback(() => {
        handleBulkApprove(props, feedback)
    }, [handleBulkApprove, props, feedback])

    return (
        <>
            <Typogaphy size="small" className="text-yellow-500">
                {t('approved-description')}
            </Typogaphy>

            <div className="mt-4">
                <Typogaphy size="small" className="mb-2">
                    {t('business-select')}
                </Typogaphy>

                <DataTableCustomized
                    pagination={pagination}
                    columns={columns}
                    rows={rows || []}
                    rowKey={getRowKey}
                />
                <TextareaAutoResize
                    label={t('feedback')}
                    className="mt-4"
                    placeholder={t('reason-cancel')}
                    value={feedback}
                    onChange={handleFeedbackChange}
                />
            </div>

            <div className="flex justify-end space-x-2 mt-4">
                <Button variant="outlined" size="small" onClick={onClose}>
                    {t('cancle-operation')}
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    loading={isPending}
                    disabled={isPending}
                    onClick={onApprove}
                >
                    {t('confirm')}
                </Button>
            </div>
        </>
    )
}

// Main modal
const BusinessApprovalModal: React.FC<BusinessApprovalModalProps> = (props) => {
    const { t } = useTranslation('business')
    const { onClose, open, mode } = props

    const title = useMemo(() => {
        if (mode === 'bulk') return t('approved-title')
        const singleProps = props as SingleApprovalProps
        return singleProps.type === 'leader'
            ? t('leader-confirm')
            : t('bom/bod-confirm')
    }, [mode, props, t])

    const dialogClassName =
        mode === 'bulk'
            ? 'max-h-[80vh] overflow-y-auto min-w-[600px]'
            : undefined

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={title}
            className={dialogClassName}
            actions={
                mode === 'single' ? (
                    <div className="flex items-end space-x-2">
                        <Button size="small" variant="text" onClick={onClose}>
                            {t('close')}
                        </Button>
                    </div>
                ) : undefined
            }
        >
            {mode === 'single' ? (
                <SingleApprovalContent
                    {...(props as SingleApprovalProps)}
                    onClose={onClose}
                />
            ) : (
                <BulkApprovalContent
                    {...(props as BulkApprovalProps)}
                    onClose={onClose}
                />
            )}
        </Dialog>
    )
}

export default memo(BusinessApprovalModal)
export type {
    BusinessApprovalModalProps,
    SingleApprovalProps,
    BulkApprovalProps,
}
